import axios from 'axios'
import { SEARCH_TOKEN_URL } from '../libs/config'

export interface AnswerHit {
  objectID: number
  title: string
  url?: string
  topic_url?: string
  id?: string
  userid?: number
  username?: string
  author_avatar?: string
  source?: string
  _highlightResult: unknown
  _snippetResult: {
    title?: {value: string, matchLevel: 'full'|'none'}
    first_post?: {value: string, matchLevel: 'full'|'none'}
    content?: {value: string, matchLevel: 'full'|'none'}
    reply?: {value: string, matchLevel: 'full'|'none'}
    answer?: {value: string, matchLevel: 'full'|'none'}
  }
  _answer: {
    extract: string
    extractAttribute: string
    score: number
  }
}

export type AnswerHits = AnswerHit[]

export type AlgoliaAnswerCallback = (hit: AnswerHit | null) => void

export async function algoliaAnswer (
  applicationId: string,
  apiKey: string,
  indexes: string[],
  input: string
): Promise<AnswerHit | null> {
  const data = {
    query: input,
    queryLanguages: ['en'],
    params: {
      distinct: false
    },
    attributesForPrediction: ['answer', 'first_post', 'reply', 'content', 'title'],
    nbHits: 1
  }

  // overwrite default headers set in entry file
  const axiosInstance = axios.create({ headers: null })

  const res = await Promise.allSettled(
    indexes.map(async index => await axiosInstance({
      method: 'POST',
      url: `https://${applicationId}-dsn.algolia.net/1/answers/${index}/prediction`,
      headers: {
        'X-Algolia-Application-Id': applicationId,
        'X-Algolia-API-Key': apiKey
      },
      data: JSON.stringify(data)
    })))

  return res
    .map(result => {
      if (result.status !== 'fulfilled') return []
      const hits: AnswerHits = result.value.data.hits
      return hits
    })
    .reduce((best: AnswerHit | null, result: AnswerHits) => {
      if (!best) return result[0]
      if (!result.length) return best
      let newBest = best
      for (const hit of result) {
        if (hit._answer.score > newBest._answer.score) newBest = hit
      }
      return newBest
    }, null)
}

function getCookieValue (a: string) {
  const match = document.cookie.match('(^|;)\\s*' + a + '\\s*=\\s*([^;]+)')
  return parseInt(match ? match.pop() || '0' : '0')
}

export function insights (method: string, payload: unknown) {
  if (typeof window !== 'undefined') {
    // @ts-expect-error
    import('search-insights/dist/search-insights-browser.cjs.min')
      .then(({ default: searchInsights }: { default: (method: string, payload: unknown) => void }) => {
        if (method === 'init' && getCookieValue('cookiePrivacyLevel') < 3) {
          payload = Object.assign(payload, { userHasOptedOut: true })
        }
        searchInsights(method, payload)
      })
  }
}

export interface Credentials {
  client_id: string
  token: string
  basicToken: string
  isZendeskFederatedSearchEnabled: boolean
  hiddenCategories: number[]
}

export async function getSearchToken (cb: (credentials: Credentials) => void) {
  async function provideCredentials () {
    const data: Credentials = (await axios.get(SEARCH_TOKEN_URL, {})).data
    cb(data)
  }
  setInterval(provideCredentials, 1000 * 60 * 25)
  await provideCredentials()
}
